import { useState } from 'react'
import { Download, FileText, Globe, Printer, X } from 'lucide-react'
import type { PresentationData } from '../../types'
import { exportAsPDF, exportAsHTML } from '../../utils/exportPresentation'

interface ExportModalProps {
  isOpen: boolean
  onClose: () => void
  presentation: PresentationData
}

export function ExportModal({ isOpen, onClose, presentation }: ExportModalProps) {
  const [isExporting, setIsExporting] = useState(false)
  const [exportFormat, setExportFormat] = useState<'pdf' | 'html'>('html')

  const handleExport = async (format: 'pdf' | 'html') => {
    setIsExporting(true)
    setExportFormat(format)

    try {
      if (format === 'pdf') {
        await exportAsPDF(presentation)
      } else {
        await exportAsHTML(presentation)
      }
      // Close modal after successful export
      setTimeout(() => {
        onClose()
        setIsExporting(false)
      }, 500)
    } catch (error) {
      console.error('Export failed:', error)
      alert(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
      setIsExporting(false)
    }
  }

  const handlePrint = () => {
    handleExport('pdf')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-2xl border border-border w-full max-w-md animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-foreground">Export Presentation</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {presentation.metadata.title}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isExporting}
            className="p-2 hover:bg-muted rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            Choose an export format. All exports work offline and don't require a backend connection.
          </p>

          {/* Export Options */}
          <div className="space-y-3">
            {/* HTML Export */}
            <button
              onClick={() => handleExport('html')}
              disabled={isExporting}
              className="w-full flex items-center gap-4 p-4 rounded-lg border-2 border-border hover:border-primary hover:bg-primary/5 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
            >
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-foreground">Standalone HTML</h3>
                <p className="text-sm text-muted-foreground">
                  Self-contained file with all styles. Works in any browser.
                </p>
              </div>
              <Download className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
            </button>

            {/* PDF Export */}
            <button
              onClick={() => handleExport('pdf')}
              disabled={isExporting}
              className="w-full flex items-center gap-4 p-4 rounded-lg border-2 border-border hover:border-primary hover:bg-primary/5 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
            >
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-foreground">PDF</h3>
                <p className="text-sm text-muted-foreground">
                  Opens print dialog. Save as PDF from there.
                </p>
              </div>
              <Download className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
            </button>

            {/* Quick Print */}
            <button
              onClick={handlePrint}
              disabled={isExporting}
              className="w-full flex items-center gap-4 p-4 rounded-lg border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
            >
              <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center group-hover:scale-110 transition-transform">
                <Printer className="w-6 h-6 text-muted-foreground" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-foreground">Quick Print</h3>
                <p className="text-sm text-muted-foreground">
                  Open print dialog directly
                </p>
              </div>
            </button>
          </div>

          {/* Info Box */}
          <div className="mt-4 p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground">
            <p>💡 <strong>Tip:</strong> For best results, use "Save as PDF" from the print dialog. The HTML export is perfect for sharing or presenting without an internet connection.</p>
          </div>
        </div>

        {/* Footer */}
        {isExporting && (
          <div className="px-6 pb-6">
            <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span>Exporting as {exportFormat.toUpperCase()}...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
